import { ColumnDef, ColumnHelper, createColumnHelper, RowData } from '@tanstack/react-table';

/**
 * Defines a table's columns with full per-column type-checking and returns them
 * widened to `ColumnDef<TData, unknown>[]` for `TanstackTable`.
 *
 * `ColumnDef` is invariant in its value type, so a heterogeneous column list has
 * no single value type. `defineColumns` centralizes that one unavoidable widening
 * here, instead of every call site writing `as ColumnDef<TData, unknown>[]`.
 * Inside `build`, every `column.accessor(...)` is still checked against `TData`
 * (key existence and cell value type), so no type information is lost.
 *
 * ```tsx
 * const columns = defineColumns<Device>((column) => [
 *   column.accessor('name', { cell: (info) => info.getValue() }),
 *   column.accessor('battery', { cell: (info) => `${info.getValue()}%` }),
 * ]);
 * ```
 */
export function defineColumns<TData extends RowData>(
  build: (column: ColumnHelper<TData>) => unknown[]
): ColumnDef<TData, unknown>[] {
  return build(createColumnHelper<TData>()) as ColumnDef<TData, unknown>[];
}
